import { useMemo } from "react";
import { Text } from "react-native";
import { useTranslation } from "react-i18next";
import { isCellAbsent, isEffortAxis, kindAt, rowsAtWeek, type SlotDraft, type UnitsPreference } from "@heraion/shared";

import { ComposerSetTable } from "../ProgramComposer/ComposerSetTable";
import { makeComposerStyles } from "../ProgramComposer/composerStyles";
import { LoadAxisSelector } from "../ProgramComposer/prescribe/LoadAxisSelector";
import { makeEditorStyles } from "../ProgramEditor/styles";
import { useFocusChain } from "../primitives/useFocusChain";
import { useRevealFocused } from "./useRevealFocusedField";
import type { UiTheme } from "../theme";
import type { V3ExerciseActions } from "./useV3ExerciseActions";
import type { V3Styles } from "./v3Styles";

// The open round of the ladder (#336): today's controls, as they are
// (decision 11/10). LoadAxisSelector above ComposerSetTable, which brings
// ComposerSetRow (reps with the "+" AMRAP and its stop rule, value, − set), the Time line
// (bound by reps/time/open, seconds), the rep range behind its «Range» chip
// (4d, S3), the pause inside the rep behind its «Pause» chip (HI-8), the
// per-set note of this round behind its «Note» chip, and «Add
// set». The table speaks block rows; `rowsAtWeek`
// reads the round (only the sets it has, S1), the actions write it back.
export function RoundSetsEditor({
  theme,
  styles,
  slot,
  slotIdx,
  week,
  cellUnit,
  actions,
}: {
  theme: UiTheme;
  styles: V3Styles;
  slot: SlotDraft;
  slotIdx: number;
  week: number;
  cellUnit: UnitsPreference;
  actions: V3ExerciseActions;
}) {
  const { t } = useTranslation();
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);
  const selectStyles = useMemo(() => makeEditorStyles(p), [p]);
  const write = actions.round(slotIdx, week);
  // The note as stored, not trimmed as rowsAtWeek reads it: the field is
  // typed into, and a trimmed echo would eat the space between two words.
  const rows = rowsAtWeek(slot, week).map((r, i) => ({
    ...r,
    note: slot.setRows[i]?.notesPerCell[week] ?? r.note,
  }));
  // Two fields a set, reps then value: the keyboard's "next" walks them.
  const chain = useFocusChain(rows.length * 2, useRevealFocused());
  // This round's axis (#336: a round may hold its own); a pick sets every round.
  // Read from the sets this round has (S1): an absent row's axis is not its.
  // RPE and RIR sets together are one effort axis (#432).
  const kinds = [...new Set(slot.setRows.filter((r) => !isCellAbsent(r, week)).map((r) => kindAt(r, week)))];
  const kind = kinds.length === 1 ? kinds[0]! : kinds.length > 0 && kinds.every(isEffortAxis) ? write.scale : null;
  return (
    <>
      <ComposerSetTable
        members={[
          {
            exerciseId: slot.exerciseId,
            exerciseName: slot.exerciseName,
            setRows: rows,
            instructions: slot.instructions,
            amrapLastSet: slot.amrapLastSet,
            maxAttempt: slot.maxAttempt,
            rpeEnabled: slot.rpeEnabled,
            rirEnabled: slot.rirEnabled,
          },
        ]}
        grouped={false}
        hideMemberHeads
        noteToggle
        rangeToggle
        amrapStop
        pauseToggle
        axisSlot={
          <LoadAxisSelector
            theme={theme}
            kind={kind}
            scale={write.scale}
            shownEffort={kinds.length === 1 && (kinds[0] === "rpe" || kinds[0] === "rir") ? kinds[0] : undefined}
            onSelect={write.setAxis}
            cellUnit={cellUnit}
          />
        }
        fieldProps={(rowIdx, field) => chain.fieldProps(rowIdx * 2 + (field === "reps" ? 0 : 1))}
        onUpdateRow={(_m, rowIdx, patch) => write.updateRow(rowIdx, patch)}
        onSetKind={(_m, k) => write.setAxis(k)}
        onAddRound={write.addSet}
        onRemoveRound={write.removeSet}
        // Member heads are hidden: the exercise view carries these.
        onRemoveMember={() => undefined}
        onReplaceMember={() => undefined}
        onSetInstructions={() => undefined}
        cellUnit={cellUnit}
        styles={cs}
        selectStyles={selectStyles}
        theme={theme}
      />
      {/* The axis and the time bound belong to the exercise, not to one
          round; the set count too, unless the round was opened from the
          «Per round» lens (#336 S1). Said here, before the tap. */}
      <Text style={styles.muted}>
        {write.onlyThisRound
          ? t("editorV3.roundScopeLens", {
              defaultValue:
                "Reps, load, seconds and sets change this round only. Axis and time bound change every round.",
            })
          : t("editorV3.roundScope", {
              defaultValue:
                "Reps, load and seconds change this round only. Axis, time bound and sets change every round.",
            })}
      </Text>
    </>
  );
}
